import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Checkbox } from '@cencosud-cencommerce/paris-uikit';
import { yupResolver } from '@hookform/resolvers/yup';

import { useAppDispatch, useAppSelector } from '@/Application/Hooks/Redux';
import {
  CardInput,
  CardSelect,
  Feedback,
  FeedbackType,
  PaymentButton,
  PreviewSavedCard,
  Terms
} from '@/modules/perseo-billing/components';
import { gradientStyle } from '@/modules/perseo-billing/containers/payment-methods/components/payment-method/saved-cards/components/utils';
import { schema } from '@/modules/perseo-billing/containers/payment-methods/components/payment-method/saved-cards/saved-card.billing.schema';
import type { TTransformedFees } from '@/modules/perseo-billing/containers/payment-methods/interfaces';
import { getTransformedFees } from '@/modules/perseo-billing/containers/payment-methods/utils';
import {
  useClearPaymentMethod,
  useGetCartTotals
} from '@/modules/perseo-billing/hooks';
import type {
  ISavedCardResponse,
  TCardInputs
} from '@/modules/perseo-billing/interfaces';
import type { ISinglePayment } from '@/modules/perseo-billing/interfaces/src/bff-web/billing/interfaces/payment-methods.interfaceV2';
import { PaymentProcessor } from '@/modules/perseo-billing/interfaces/src/bff-web/billing/interfaces/payment-methods.interfaceV2';
import {
  setPaymentAvailable,
  setSelectedPaymentMethods
} from '@/modules/perseo-billing/store/slices/billing';
import {
  Icon,
  Typography,
  useMediaQuery
} from '@/modules/perseo-billing/ui/react/src';
import { encryptData } from '@/modules/perseo-billing/utils';
import { LocalHookFormDevtool } from '@/sections/shared/hooks/local-hookform-devtool';

type TFocusedInput = TCardInputs | undefined;

const DEFAULT_VALUES = {
  cvv: '',
  installments: 1,
  termsAndConditions: true
};

export const SavedCardBody = (props: {
  savedCard: ISavedCardResponse;
  isActive: boolean;
}) => {
  const dispatch = useAppDispatch();
  const { savedCard } = props;

  const { data: cart } = useAppSelector((state) => state.cart);
  const billing = useAppSelector((state) => state?.billing);

  const totals = useGetCartTotals();
  const { otherMethods, cencosudCard } = totals;

  const { availablePayments } = useAppSelector((state) => state.billing);

  const { cencoFees, creditFees } = availablePayments || {};

  const savedCardsForm = useForm({
    resolver: yupResolver(schema),
    defaultValues: DEFAULT_VALUES
  });

  const { clearPayment } = useClearPaymentMethod();
  const publicKey = billing?.availablePayments?.sessionKey?.publicKey ?? '';

  const {
    watch,
    formState: { errors, isValid, touchedFields, dirtyFields },
    setValue,
    register
  } = savedCardsForm;

  const isCencosud =
    (savedCard?.methodId as string) ===
    PaymentProcessor.TRANSBANK_CENCOSUD_CARD;

  const [focusedInput, setFocusedInput] = useState<TFocusedInput>(undefined);
  const [isCvvHidden, setIsCvvHidden] = useState(true);

  const [installmentPayments] = useState<TTransformedFees[]>(() =>
    getTransformedFees(isCencosud ? cencoFees : creditFees)
  );
  const isMobile = useMediaQuery('lg');

  const isFieldValid = (name: keyof typeof touchedFields) =>
    Boolean(touchedFields[name]) && Boolean(dirtyFields[name]) && !errors[name];

  const installmentsWatch = watch('installments');
  const cvvWatch = watch('cvv');
  const isAccepted = watch('termsAndConditions');

  const gradientClass = gradientStyle(true, false, false);

  const withGiftCardPrice = totals?.hasGiftCardApplied;
  const cencosudPrice = withGiftCardPrice
    ? withGiftCardPrice
    : cencosudCard?.price;

  const otherMethodsPrice = withGiftCardPrice
    ? withGiftCardPrice
    : otherMethods?.price;

  const totalPrice = isCencosud ? cencosudPrice : otherMethodsPrice;

  const labelPrice = totalPrice ? `Pagar ${totalPrice}` : 'Pagar';

  useEffect(() => {
    setValue('installments', 1, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true
    });
  }, [setValue, installmentPayments]);

  useEffect(() => {
    if (!isValid && cvvWatch) {
      clearPayment();
    }
  }, [clearPayment, isValid, cvvWatch]);

  useEffect(() => {
    if (!cart) {
      dispatch(setPaymentAvailable(false));
      return;
    }
    if (!isValid) {
      dispatch(setPaymentAvailable(false));
      return;
    }

    if (!isAccepted) {
      dispatch(setPaymentAvailable(false));
      return;
    }

    const paymentMethodToAdd: ISinglePayment = {
      paymentProcessor: isCencosud
        ? PaymentProcessor.TRANSBANK_CENCOSUD_CARD
        : PaymentProcessor.TRANSBANK,
      paymentToken: savedCard?.token,
      encryptedCVV: encryptData(String(cvvWatch), publicKey),
      installments: Number(installmentsWatch) ?? 1,
      isNewCard: false,
      saveCard: false
    };

    dispatch(
      setSelectedPaymentMethods({
        payments: [paymentMethodToAdd]
      })
    );

    dispatch(setPaymentAvailable(true));
  }, [
    dispatch,
    installmentsWatch,
    isAccepted,
    isValid,
    cvvWatch,
    cart,
    savedCard?.token,
    publicKey,
    isCencosud
  ]);

  return (
    <div
      className={`co-w-full co-h-auto co-flex co-flex-col co-rounded-br co-rounded-bl ${gradientClass}`}
    >
      <div className="co-flex co-flex-col-reverse xl:co-flex-row co-m-2px co-mt-0 co-px-24px co-pt-16px co-pb-32px co-gap-24px co-rounded-b co-bg-white co-items-center">
        <FormProvider {...savedCardsForm}>
          {savedCard?.isExpired ? (
            <ExpiredCardBody />
          ) : (
            <form className="co-flex co-flex-col co-gap-24px">
              <div className="co-flex co-gap-16px">
                <div className="co-flex co-w-2/5">
                  <CardInput
                    onFocus={() => setFocusedInput('cvv')}
                    type={isCvvHidden ? 'password' : 'text'}
                    maxLength={4}
                    autoComplete="cc-csc"
                    label="Código CVV"
                    error={errors?.cvv?.message?.toString()}
                    endIcon={
                      <Icon icon={isCvvHidden ? 'EyeBlockIcon' : 'EyeIcon'} />
                    }
                    onEndIconClick={() => setIsCvvHidden(!isCvvHidden)}
                    example="3 dígitos al reverso"
                    labelSize="body2"
                    isValid={isFieldValid('cvv')}
                    {...register('cvv')}
                    onBlur={() => {
                      setFocusedInput('cardNumber');
                    }}
                  />
                </div>
                <div className="co-flex co-flex-1 co-flex-col">
                  <CardSelect
                    error={errors?.installments?.message?.toString()}
                    color={installmentsWatch ? 'success' : 'default'}
                    options={installmentPayments}
                    label={'Número de cuotas'}
                    labelSize="body2"
                    {...register('installments', {
                      onChange: (e) => {
                        setValue('installments', e.target.value);
                      }
                    })}
                  />
                </div>
              </div>

              <Checkbox
                {...register('termsAndConditions', {
                  onChange: (e) => {
                    setValue('termsAndConditions', e.target.checked);
                  }
                })}
                label={<Terms />}
                defaultChecked={isAccepted}
              />
              {!isMobile && (
                <PaymentButton
                  label={labelPrice}
                  method={isCencosud ? 'EPAY_CENCOSUD_TCOM' : 'EPAY_TCOM'}
                  disabled={!isValid || savedCard?.isExpired}
                />
              )}
              <LocalHookFormDevtool control={savedCardsForm.control} />
            </form>
          )}
          <PreviewSavedCard {...props} focusedInput={focusedInput} />
        </FormProvider>
      </div>
    </div>
  );
};

const ExpiredCardBody = () => {
  return (
    <div className="co-w-1/2 co-flex co-flex-col co-gap-24px">
      <Feedback
        type={FeedbackType.WARNING}
        title={'Tu tarjeta expiró'}
        subtitle={
          'Ponte en contacto con tu banco o prueba con otro medio de pago.'
        }
      />
      <Typography noWrap>
        {'Si quieres eliminar esta tarjeta, ingresa a tu cuenta en la sección '}
        <span className="co-font-bold">“Tarjetas guardadas”</span>
      </Typography>
    </div>
  );
};
